// Générateur de feuille d'arbitre simplifié
import { MatchState } from '../types';
import { RefereeSheetOptions, GenerateSheetResult } from '../types/refereeSheet';

// 📋 Réutiliser la fonction existante de useResultSender
// On va l'importer depuis son module
import { generateResultsJSON } from '../hooks/useResultSender';

// 🔄 Version compatible CRA + Webpack futur
export async function loadTemplate(): Promise<string> {
  try {
    // 🔧 SOLUTION HYBRIDE compatible CRA + Webpack
    
    // Méthode 1: Import statique (fonctionne avec Webpack + bundlers modernes)
    try {
      // Import dynamique du template depuis src/assets/
      //const templateModule = await import('../assets/templates/referee-sheet.html');
      //const template = templateModule.default || templateModule;
      
      //if (typeof template === 'string' && template.length > 0) {
      //  console.log('✅ Template loaded via dynamic import');
      //  return template;
     // }
    } catch (importError) {
      console.log('ℹ️ Dynamic import failed, trying fetch fallback');
    }
    
    // Méthode 2: Fetch fallback (CRA et serveurs de dev)
    // Essayer plusieurs chemins pour supporter dev et prod
    const pathsToTry = [
      `${process.env.PUBLIC_URL || ''}/assets/templates/referee-sheet.html`,
      '/assets/templates/referee-sheet.html',
      './assets/templates/referee-sheet.html',
    ];

    for (const templateUrl of pathsToTry) {
      try {
        const response = await fetch(templateUrl);
        if (response.ok) {
          const template = await response.text();
          console.log(`✅ Template loaded via fetch from: ${templateUrl}`);
          return template;
        }
      } catch (fetchError) {
        // Continue to next path
      }
    }

    throw new Error('Failed to load template from any path');
    
  } catch (error) {
    console.error('❌ Error loading referee sheet template:', error);
    throw new Error('Template loading failed');
  }
}

// 📅 Utilitaire : Formater la date (YYYY-MM-DD HH:MM → DD/MM/YYYY et HH:MM)
function formatDateTime(dateTimeStr: string): { date: string; time: string } {
  try {
    const [dateStr, timeStr] = dateTimeStr.split(' ');
    const [year, month, day] = dateStr.split('-');
    return {
      date: `${day}/${month}/${year}`,
      time: timeStr || ''
    };
  } catch {
    return { date: dateTimeStr, time: '' };
  }
}

/**
 * Escape a value for HTML text. The sheet is rendered with dangerouslySetInnerHTML and printed
 * through document.write, in the app's origin: names, competition, referee... come from the
 * launch link, so markup in them would run there (threat model SCN-12).
 */
export function escapeHtml(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c] as string));
}

// 🎯 ÉTAPE 2: Remplir le template avec les données JSON existantes
export function fillTemplate(template: string, resultsData: any, options: RefereeSheetOptions = {}): string {
  // Options par défaut
  const opts = {
    includeEmptyRows: true,
    emptyRowsCount: 15,
    ...options
  };

  // 📝 Extraire les données du JSON existant
  const { date, time } = formatDateTime(resultsData.match.dateTime);

  // 📝 Valeurs texte, échappées
  const textValues: Record<string, unknown> = {
    COMPETITION_NAME: resultsData.match.competition,
    CATEGORY_NAME: resultsData.match.category,
    DRAW_NAME: resultsData.match.draw,
    ROUND_TYPE: resultsData.match.roundType,
    COURT_NAME: resultsData.match.court,
    REFEREE_NAME: resultsData.match.referee,
    MATCH_DATE: date,
    MATCH_TIME: time,
    PLAYER_A_NAME: resultsData.players.A.name,
    PLAYER_B_NAME: resultsData.players.B.name,
  };

  // 🏆 Scores individuels de chaque jeu (1-5)
  for (let gameNum = 1; gameNum <= 5; gameNum++) {
    const game = resultsData.games[gameNum - 1];
    textValues[`GAME_${gameNum}_SCORE_A`] = game?.scoreA;
    textValues[`GAME_${gameNum}_SCORE_B`] = game?.scoreB;
  }

  // 📊 Fragments HTML générés ici, valeurs échappées à l'intérieur
  const scoreCells = (side: 'scoreA' | 'scoreB') => Array.from({ length: 5 }, (_, i) =>
    `<td>${escapeHtml(resultsData.games[i]?.[side])}</td>`).join('');
  const htmlValues: Record<string, string> = {
    GAME_SCORES_A: scoreCells('scoreA'),
    GAME_SCORES_B: scoreCells('scoreB'),
    POINTS_ROWS: generatePointsRowsHTML(resultsData.points || [], opts),
  };

  // Une seule passe : une valeur qui contiendrait "{{...}}" n'est jamais remplacée à son tour,
  // et une fonction de remplacement ignore les motifs "$&" / "$1" qu'une chaîne interpréterait
  return template.replace(/\{\{([A-Z0-9_]+)\}\}/g, (placeholder, key: string) => {
    if (key in htmlValues) return htmlValues[key];
    if (key in textValues) return escapeHtml(textValues[key]);
    return placeholder;
  });
}

// 🎯 Générer le HTML des lignes de points (utilise les données JSON existantes)
function generatePointsRowsHTML(points: any[], options: RefereeSheetOptions): string {
  const rows: string[] = [];
  
  // 🎮 Organiser les points par jeu
  const pointsByGame: { [gameNumber: number]: any[] } = {};
  points.forEach(point => {
    const gameNum = point.gameNumber || 1;
    if (!pointsByGame[gameNum]) {
      pointsByGame[gameNum] = [];
    }
    pointsByGame[gameNum].push(point);
  });

  // 📏 Calculer le nombre de lignes nécessaires
  const maxPointsInAnyGame = Math.max(
    ...Object.values(pointsByGame).map(gamePoints => gamePoints.length),
    options.emptyRowsCount || 15
  );

  // 🔄 Générer une ligne HTML pour chaque point
  for (let rowIndex = 0; rowIndex < maxPointsInAnyGame - 1; rowIndex++) {
    const cells: string[] = [];
    
    // Pour chaque jeu (1-5)
    for (let gameNum = 1; gameNum <= 5; gameNum++) {
      const gamePoints = pointsByGame[gameNum] || [];
      const point = gamePoints[rowIndex];
      
      // Classes CSS pour les bordures
      const isLastRow = rowIndex === maxPointsInAnyGame - 1;
      const leftClass = isLastRow ? 'pointCellLeftLast' : 'pointCellLeft';
      const rightClass = isLastRow ? 'pointCellRightLast' : 'pointCellRight';
      const separatorClass = gameNum > 1 ? 'gameSeparator ' : '';
      

      if (point && point.servingPlayer && point.servingSide) {
        // ✅ LOGIQUE CORRIGÉE : 4 colonnes par jeu avec classes CSS correctes
        const { aLeft, aRight, bLeft, bRight } = calculateGameScoreDisplay(point);
        
        // Colonne A gauche (avec séparateur si pas le 1er jeu)
        cells.push(`<td class="${separatorClass}${leftClass}">${aLeft}</td>`);
        // Colonne A droite
        cells.push(`<td class="${rightClass}">${aRight}</td>`);
        // Colonne B gauche (toujours sans séparateur car dans le même jeu)
        cells.push(`<td class="${leftClass}">${bLeft}</td>`);
        // Colonne B droite  
        cells.push(`<td class="${rightClass}">${bRight}</td>`);
      } else {
        // Cellules vides avec les bonnes classes
        cells.push(`<td class="${separatorClass}${leftClass}">&nbsp;</td>`);
        cells.push(`<td class="${rightClass}">&nbsp;</td>`);
        cells.push(`<td class="${leftClass}">&nbsp;</td>`);
        cells.push(`<td class="${rightClass}">&nbsp;</td>`);
      }
    }
    
    rows.push(`<tr class="body-row">${cells.join('')}</tr>`);
  }
  
  return rows.join('\n');
}

// 🎯 Calculer l'affichage pour les 4 colonnes d'un jeu
function calculateGameScoreDisplay(point: any): { 
  aLeft: string, aRight: string, bLeft: string, bRight: string 
} {
  let aLeft = '&nbsp;';   // A service à gauche
  let aRight = '&nbsp;';  // A service à droite
  let bLeft = '&nbsp;';   // B service à gauche  
  let bRight = '&nbsp;';  // B service à droite
  
  // Placer le score dans la bonne colonne selon qui sert et de quel côté
  if (point.servingPlayer === 'A') {
    if (point.servingSide === 'left') {
      aLeft = escapeHtml(point.scoreA) || '&nbsp;';
    } else if (point.servingSide === 'right') {
      aRight = escapeHtml(point.scoreA) || '&nbsp;';
    }
  } else if (point.servingPlayer === 'B') {
    if (point.servingSide === 'left') {
      bLeft = escapeHtml(point.scoreB) || '&nbsp;';
    } else if (point.servingSide === 'right') {
      bRight = escapeHtml(point.scoreB) || '&nbsp;';
    }
  }
  
  return { aLeft, aRight, bLeft, bRight };
}

// 🚀 FONCTION PRINCIPALE: Générer la feuille complète
export async function generateRefereeSheet(
  matchState: MatchState, 
  options: RefereeSheetOptions = {}
): Promise<GenerateSheetResult> {
  try {
    console.log('🔄 Generating referee sheet...');
    
    // Étape 1: Réutiliser le JSON existant (même fonction que useResultSender)
    const resultsData = generateResultsJSON(matchState, 'completed') as any;
    console.log('✅ Using existing JSON data:', { 
    games: resultsData.games?.length || 0, 
    points: resultsData.points?.length || 0 
    });
    
    // Étape 2: Charger le template
    const template = await loadTemplate();
    console.log('✅ Template loaded:', template.length, 'characters');
    
    // Étape 3: Remplir le template
    const filledHtml = fillTemplate(template, resultsData, options);
    console.log('✅ Template filled successfully');
    
    return {
      success: true,
      html: filledHtml
    };
    
  } catch (error) {
    console.error('❌ Error generating referee sheet:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}